import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { CheckCheck, Trash2, Settings, FileText, Lock, Upload, MessageSquare, CheckCircle, Wrench, AlertTriangle, Play, Flag, Clock, Lightbulb, Globe, FileBarChart } from 'lucide-react'
import { useNotifications, Notification } from '@/hooks/useNotifications'
import { formatDistanceToNow } from 'date-fns'
import { enUS } from 'date-fns/locale'

interface NotificationDropdownProps {
  onClose: () => void
}

export const NotificationDropdown = ({ onClose }: NotificationDropdownProps) => {
  const navigate = useNavigate()
  const { notifications, loading, markAsRead, markAllAsRead, deleteNotification } = useNotifications()

  const recentNotifications = notifications.slice(0, 5)

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'phase_completed':
        return <Lock className="w-4 h-4 text-framed-neutral-600" />
      case 'project_submitted':
        return <FileText className="w-4 h-4 text-framed-neutral-600" />
      case 'file_uploaded':
        return <Upload className="w-4 h-4 text-framed-neutral-600" />
      case 'comment_added':
        return <MessageSquare className="w-4 h-4 text-framed-neutral-600" />
      case 'comment_resolved':
        return <CheckCircle className="w-4 h-4 text-emerald-600" />
      case 'project_status_changed':
        return <FileText className="w-4 h-4 text-framed-neutral-600" />
      case 'feedback_round_created':
        return <Play className="w-4 h-4 text-blue-600" />
      case 'feedback_round_completed':
        return <CheckCircle className="w-4 h-4 text-emerald-600" />
      case 'project_completed':
        return <CheckCircle className="w-4 h-4 text-emerald-600" />
      case 'feedback_batch_summary':
        return <MessageSquare className="w-4 h-4 text-framed-neutral-600" />
      // Phase 5 notification types
      case 'maintenance_item_created':
        return <Wrench className="w-4 h-4 text-framed-neutral-600" />
      case 'maintenance_item_completed':
        return <CheckCircle className="w-4 h-4 text-emerald-600" />
      case 'maintenance_priority_alert':
        return <AlertTriangle className="w-4 h-4 text-amber-600" />
      case 'maintenance_cycle_started':
        return <Play className="w-4 h-4 text-blue-600" />
      case 'maintenance_cycle_closed':
        return <Flag className="w-4 h-4 text-emerald-600" />
      // Phase 5 extended notification types
      case 'hour_package_low':
        return <Clock className="w-4 h-4 text-amber-600" />
      case 'time_entry_added':
        return <Clock className="w-4 h-4 text-framed-neutral-600" />
      case 'suggestion_proposed':
        return <Lightbulb className="w-4 h-4 text-blue-600" />
      case 'suggestion_accepted':
        return <Lightbulb className="w-4 h-4 text-emerald-600" />
      case 'suggestion_rejected':
        return <Lightbulb className="w-4 h-4 text-framed-neutral-500" />
      case 'domain_expiry_warning':
        return <Globe className="w-4 h-4 text-amber-600" />
      case 'monthly_report_ready':
        return <FileBarChart className="w-4 h-4 text-blue-600" />
      default:
        return <FileText className="w-4 h-4 text-framed-neutral-600" />
    }
  }

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read
    if (!notification.read) {
      markAsRead(notification.id)
    }

    // Navigate to link if exists
    if (notification.link) {
      navigate(notification.link)
      onClose()
    }
  }

  const handleMarkAllAsRead = async () => {
    await markAllAsRead()
  }

  const handleDelete = async (notificationId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    await deleteNotification(notificationId)
  }

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -10, scale: 0.95 }}
        transition={{ duration: 0.15 }}
        className="absolute right-0 top-12 w-96 bg-white rounded-xl shadow-xl border border-framed-neutral-300 p-4"
      >
        <div className="flex items-center justify-center py-4">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            className="w-5 h-5 border-2 border-framed-neutral-400 border-t-framed-neutral-600 rounded-full"
          />
        </div>
      </motion.div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10, scale: 0.95 }}
      transition={{ duration: 0.15 }}
      className="absolute right-0 top-12 w-96 bg-white rounded-xl shadow-xl border border-framed-neutral-300 overflow-hidden z-50"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-framed-neutral-200">
        <h3 className="font-semibold text-framed-neutral-800">Notifications</h3>
        <div className="flex items-center gap-2">
          {notifications.some((n) => !n.read) && (
            <button
              onClick={handleMarkAllAsRead}
              className="text-xs text-framed-neutral-600 hover:text-framed-neutral-800 flex items-center gap-1 transition-colors"
              title="Mark all as read"
            >
              <CheckCheck className="w-3 h-3" />
              Mark all read
            </button>
          )}
          <button
            onClick={() => {
              navigate('/settings/notifications')
              onClose()
            }}
            className="p-1.5 text-framed-neutral-500 hover:text-framed-neutral-800 hover:bg-framed-neutral-200 rounded-lg transition-colors"
            title="Settings"
          >
            <Settings className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Notifications list */}
      <div className="max-h-96 overflow-y-auto">
        {recentNotifications.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-framed-neutral-200 flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-framed-neutral-500" />
            </div>
            <p className="text-sm text-framed-neutral-500">No notifications yet</p>
            <p className="text-xs text-framed-neutral-500 mt-1">You're all caught up!</p>
          </div>
        ) : (
          <div className="divide-y divide-framed-neutral-200">
            <AnimatePresence>
              {recentNotifications.map((notification) => (
                <motion.div
                  key={notification.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, height: 0 }}
                  onClick={() => handleNotificationClick(notification)}
                  className={`p-4 hover:bg-framed-neutral-50 transition-colors cursor-pointer group ${
                    !notification.read ? 'bg-framed-neutral-50' : ''
                  }`}
                >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        !notification.read ? 'bg-framed-neutral-300' : 'bg-framed-neutral-200'
                      }`}>
                        {getNotificationIcon(notification.type)}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-medium ${!notification.read ? 'text-framed-neutral-800' : 'text-framed-neutral-700'}`}>
                          {notification.title}
                        </p>
                        {!notification.read && (
                          <span className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-1.5"></span>
                        )}
                      </div>
                      <p className="text-xs text-framed-neutral-500 mt-1 line-clamp-2">{notification.message}</p>
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-xs text-framed-neutral-500">
                          {formatDistanceToNow(new Date(notification.created_at), {
                            addSuffix: true,
                            locale: enUS,
                          })}
                        </p>
                        <button
                          onClick={(e) => handleDelete(notification.id, e)}
                          className="opacity-0 group-hover:opacity-100 p-1 text-framed-neutral-500 hover:text-red-500 hover:bg-red-50 rounded transition-all"
                          title="Delete"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Footer */}
      {notifications.length > 5 && (
        <div className="p-3 border-t border-framed-neutral-200 bg-framed-neutral-50">
          <button
            onClick={() => {
              navigate('/notifications')
              onClose()
            }}
            className="w-full text-sm text-framed-neutral-600 hover:text-framed-neutral-800 font-medium transition-colors"
          >
            View all notifications ({notifications.length})
          </button>
        </div>
      )}
    </motion.div>
  )
}
